import { FC, useRef, useMemo } from 'react'
import * as styles from 'components/features/pfm/AssetsPage/AssetsPage.css'
import AccountsRatesContainer from 'components/features/pfm/AccountsRates/AccountsRatesContainer'
import AccountsDetailsModuleContainer from 'components/features/pfm/AccountsDetailsModule/AccountsDetailsModuleContainer'
import AccountsBreakdownModuleContainer from 'components/features/pfm/AccountsBreakdown/AccountsBreakdownModuleContainer'
import { SingleColumnLayout } from 'components/layouts/Layouts'
import ModuleWrapper from 'components/features/pfm/ModuleWrapper/ModuleWrapper'
import Anchor from 'components/atoms/navigation/Anchor'
import PageFooterButton from 'components/atoms/buttons/PageFooterButton'
import TrendRankContainer from 'components/features/pfm/TrendRank/TrendRankContainer'
import { createSeeAllCategoriesButton, useShouldShowPageParamsStickyHeader } from 'helpers/pageHelpers'
import InternalLayoutContainer from 'components/features/pfm/InternalLayout/InternalLayoutContainer'
import InternalSidebarContainer from 'components/features/pfm/InternalSidebar/InternalSidebarContainer'
import { PageParamsStickyHeader } from 'components/features/pfm/PageParamsHeader/PageParamsStickyHeader'
import { DesktopInternalStickyHeader } from 'components/features/pfm/DesktopInternalStickyHeader/DesktopInternalStickyHeader'
import { PageParamsHeader } from 'components/features/pfm/PageParamsHeader/PageParamsHeader'
import { NotificationTrayWrapper } from 'components/features/pfm/NotificationTray/NotificationTrayWrapper'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import Button from 'components/features/pfm/Button/Button'
import { HomeSettingsModule } from 'components/features/pfm/HomeSettingsModule/HomeSettingsModule'
import { CarSettingsModule } from 'components/features/pfm/CarSettingsModule/CarSettingsModule'
import internal from 'components/utils/Internal'
import { HouseEditHandler } from 'components/features/pfm/HouseEditHandler/HouseEditHandler'
import { CarEditHandler } from 'components/features/pfm/CarEditHandler/CarEditHandler'
import { useSimpleGuard } from 'helpers/loadableHelpers'
import { linkedMixedAccountsGuard } from 'guards/linkageGuards'

export const AssetsPageComponent: FC = () => {
  useSimpleGuard(linkedMixedAccountsGuard, {}, 'AssetsPageComponent')

  const timeframe = useStoreSelector((state) => state.app.assets.timeframe)
  const categoryId = useStoreSelector((state) => state.app.assets.categories.selectedId)

  const isCarsCategory = useMemo(
    () => categoryId === 'all' || categoryId === 'asset_auto',
    [categoryId]
  )
  const isHomesCategory = useMemo(
    () => categoryId === 'all' || categoryId === 'home',
    [categoryId]
  )

  const linkNewHomeButton = useMemo(() => (
    <HouseEditHandler>
      <Button
        style='responsiveTextFilled'
        size='tiny'
        qaMarker='link-new-home-button'
      >
        Link New Home
      </Button>
    </HouseEditHandler>
  ), [])

  const linkNewCarButton = useMemo(() => (
    <CarEditHandler>
      <Button
        style='responsiveTextFilled'
        size='tiny'
        qaMarker='link-new-car-button'
      >
        Link New Car
      </Button>
    </CarEditHandler>
  ), [])

  const pageBody = (
    <>
      <TrendRankContainer type='assets' timeframe={ timeframe } categoryId={ categoryId } />

      <ModuleWrapper
        title={ <span>Breakdown by <span className={ styles.visibleSmallUp }>Account</span> Type</span> }
        anchor='assets_breakdown'
        card={ false }
        action={ createSeeAllCategoriesButton('assets_breakdown') }
      >
        <AccountsBreakdownModuleContainer type='assets' timeframe={ timeframe } />
      </ModuleWrapper>

      <ModuleWrapper
        title='Interest Rates'
        card={ false }
        anchor='assets_rates'
        action={ createSeeAllCategoriesButton('assets_rates') }
      >
        <AccountsRatesContainer type='assets' timeframe={ timeframe } />
      </ModuleWrapper>

      <ModuleWrapper
        title='Account Details'
        card={ false }
        anchor='assets_details'
        action={ createSeeAllCategoriesButton('assets_details') }
      >
        <AccountsDetailsModuleContainer type='assets' categoryId={ categoryId } />
      </ModuleWrapper>

      { isHomesCategory && (
        <ModuleWrapper
          title='Your Homes'
          card={ false }
          action={ linkNewHomeButton }
          anchor='assets_homes'
          qaType='module-wrapper'
          qaMarker='homes'
        >
          <HomeSettingsModule />
        </ModuleWrapper>
      )}

      { isCarsCategory && (
        <ModuleWrapper
          title='Your Cars'
          card={ false }
          action={ linkNewCarButton }
          anchor='assets_cars'
          qaType='module-wrapper'
          qaMarker='cars'
        >
          <CarSettingsModule />
        </ModuleWrapper>
      )}

      <PageFooterButton text='See Your Net Worth' to='networth' />
    </>
  )

  const staticHeaderRef = useRef<HTMLDivElement>(null)
  const shouldShowStickyHeader = useShouldShowPageParamsStickyHeader(staticHeaderRef)

  const notifications = (
    <NotificationTrayWrapper
      renderLevel='page'
      placement='assets_general'
      isPositionRelative
    />
  )

  return (
    <InternalLayoutContainer
      sidebar={ <InternalSidebarContainer /> }
      notifications={ notifications }
      stickyHeader={ <PageParamsStickyHeader type='assets' /> }
      shouldShowStickyHeader={ shouldShowStickyHeader }
    >
      <Anchor placement='assets'>
        <Anchor placement='assets_general'>
          <DesktopInternalStickyHeader isShown={ shouldShowStickyHeader }>
            <PageParamsStickyHeader type='assets' />
          </DesktopInternalStickyHeader>
          <SingleColumnLayout
            header={ <PageParamsHeader type='assets' nodeRef={ staticHeaderRef } /> }
          >
            { pageBody }
          </SingleColumnLayout>
        </Anchor>
      </Anchor>
    </InternalLayoutContainer>
  )
}

export const AssetsPage = internal(AssetsPageComponent)
